import { Plus, MessageSquare, Trash2, Edit2, History, Globe } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { Conversation } from '../types'
import type { Translation } from '../../../translations'

interface ChatSidebarProps {
    conversations: Conversation[]
    activeId: string | null
    onSelect: (id: string) => void
    onNewChat: () => void
    onDelete?: (id: string) => void
    onRename?: (id: string) => void
    t: Translation
    isOpen: boolean
    onClose: () => void
}

export function ChatSidebar({
    conversations,
    activeId,
    onSelect,
    onNewChat,
    onDelete,
    onRename,
    t,
    isOpen,
    onClose
}: ChatSidebarProps) {
    const internalNavigate = useNavigate()
    return (
        <aside
            className={`
                fixed inset-y-0 left-0 z-40 w-72 transform bg-slate-50 transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
            `}
        >
            <div className="flex h-full flex-col">
                {/* Header */}
                <div className="p-4 bg-slate-50">
                    <button
                        onClick={onNewChat}
                        className="flex w-full items-center justify-start gap-3 rounded-full bg-blue-100 px-4 py-3 text-sm font-medium text-blue-800 hover:bg-blue-200 transition-colors"
                    >
                        <Plus className="h-5 w-5" />
                        {t.chatNew}
                    </button>
                </div>

                {/* History List */}
                <div className="flex-1 overflow-y-auto custom-scrollbar px-3 pb-4">
                    <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        {t.chatRecent}
                    </div>
                    <div className="space-y-0.5">
                        {conversations.length === 0 ? (
                            <div className="px-4 py-8 text-center text-xs text-slate-400">
                                {t.noResults}
                            </div>
                        ) : (
                            conversations.map((conv) => (
                                <div
                                    key={conv.id}
                                    className={`
                                        group relative flex items-center gap-3 rounded-full px-4 py-2 text-left text-sm transition-all
                                        ${activeId === conv.id
                                            ? 'bg-blue-100 text-blue-900 font-medium'
                                            : 'text-slate-700 hover:bg-slate-200'
                                        }
                                    `}
                                >
                                    <button
                                        onClick={() => {
                                            onSelect(conv.id)
                                            if (window.innerWidth < 1024) onClose()
                                        }}
                                        className="flex-1 overflow-hidden"
                                    >
                                        <div className="flex items-center gap-2">
                                            <MessageSquare className={`h-4 w-4 shrink-0 ${activeId === conv.id ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}`} />
                                            <span className="truncate block">
                                                {conv.title || t.chatUntitled}
                                            </span>
                                        </div>
                                    </button>

                                    {/* Quick Actions (only visible on hover or if active) */}
                                    <div className="flex shrink-0 gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {onRename && (
                                            <button
                                                onClick={() => onRename(conv.id)}
                                                className="p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-300"
                                            >
                                                <Edit2 className="h-3 w-3" />
                                            </button>
                                        )}
                                        {onDelete && (
                                            <button
                                                onClick={() => onDelete(conv.id)}
                                                className="p-1 text-slate-400 hover:text-red-500 rounded-full hover:bg-slate-300"
                                            >
                                                <Trash2 className="h-3 w-3" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Bottom section (optional, e.g. status) */}
                <div className="p-4 bg-slate-50 space-y-1">
                    <div className="flex items-center gap-2 text-xs text-slate-500 px-2 cursor-pointer hover:bg-slate-200 rounded-lg py-2 transition-colors">
                        <History className="h-4 w-4" />
                        <span>{t.chatHistory}</span>
                    </div>
                    <button
                        onClick={() => internalNavigate('/e-services')}
                        className="flex w-full items-center gap-2 text-xs text-slate-500 px-2 cursor-pointer hover:bg-slate-200 rounded-lg py-2 transition-colors text-left"
                    >
                        <Globe className="h-4 w-4" />
                        <span>E-Services</span>
                    </button>
                </div>
            </div>
        </aside>
    )
}
